import { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { apiClient } from '../network/ApiClient';
import { useAuth } from './AuthContext';
import type { HomeActiveGame } from '../types/game';

/**
 * ActiveGameContext — the single source of truth for the user's current
 * resumable/reconnectable game, used by the global AppShell banner.
 *
 * Data source: `getHome().active_game` (authoritative `game_mode`, `game_id`,
 * `session_id`). No React Query in this app, so this context owns the fetch and
 * a lightweight invalidation bus:
 *   • fetch once after auth (and on login-state change)
 *   • refetch on the `active-game-changed` window event (create / leave / finish)
 *   • refetch when the tab becomes visible (cheap, catches cross-device changes)
 *   • a MODEST fallback poll — only while a NON-solo (multiplayer) game is active,
 *     because a multiplayer reconnect window can expire server-side and the
 *     banner must clear. Solo games never expire, so they are event-invalidated
 *     only (no idle polling).
 */

interface ActiveGameContextValue {
  activeGame: HomeActiveGame | null;
  loading:    boolean;
  refresh:    () => void;
}

const ActiveGameContext = createContext<ActiveGameContextValue | null>(null);

const ACTIVE_GAME_POLL_MS = 45_000;

export function ActiveGameProvider({ children }: { children: ReactNode }) {
  const { isLoggedIn } = useAuth();
  const [activeGame, setActiveGame] = useState<HomeActiveGame | null>(null);
  const [loading, setLoading]       = useState(false);
  const inFlight = useRef(false);

  const refresh = useCallback(() => {
    if (!isLoggedIn) { setActiveGame(null); return; }
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    apiClient.getHome()
      .then(data => setActiveGame(data.active_game ?? null))
      .catch(() => { /* keep the last known value on a transient error */ })
      .finally(() => { inFlight.current = false; setLoading(false); });
  }, [isLoggedIn]);

  // Fetch after auth; clear on logout.
  useEffect(() => {
    if (isLoggedIn) refresh();
    else setActiveGame(null);
  }, [isLoggedIn, refresh]);

  // Event-driven invalidation.
  useEffect(() => {
    const onChange = () => refresh();
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('active-game-changed', onChange);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('active-game-changed', onChange);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  // Bounded fallback poll — multiplayer only, only while a game is active.
  useEffect(() => {
    if (!isLoggedIn || !activeGame || activeGame.game_mode === 'bots') return;
    const t = setInterval(refresh, ACTIVE_GAME_POLL_MS);
    return () => clearInterval(t);
  }, [isLoggedIn, activeGame, refresh]);

  return (
    <ActiveGameContext.Provider value={{ activeGame, loading, refresh }}>
      {children}
    </ActiveGameContext.Provider>
  );
}

export function useActiveGame(): ActiveGameContextValue {
  const ctx = useContext(ActiveGameContext);
  if (!ctx) throw new Error('useActiveGame must be used within an ActiveGameProvider');
  return ctx;
}
